/**
 * StackingContext - Automatic z-index management for nested overlays.
 *
 * This context tracks the nesting depth of overlays (modals, slideouts, etc.)
 * and provides automatic z-index calculation so nested overlays always appear
 * above their parents.
 *
 * Usage:
 * 1. Wrap overlay content with <StackingLayer> to increment the level for children
 * 2. Use useStackingZIndex() to get the appropriate z-index for the current level
 *
 * @example
 * function Modal({ children }) {
 *   const zIndex = useStackingZIndex();
 *   return (
 *     <div style={{ zIndex }}>
 *       <StackingLayer>
 *         {children}
 *       </StackingLayer>
 *     </div>
 *   );
 * }
 */

import { createContext, type ReactNode, useContext } from 'react';

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */

// Base z-index for the first overlay level
const BASE_Z_INDEX = 400;

// How much to increment z-index per nesting level
const Z_INDEX_INCREMENT = 100;

// Context holds the current stacking level (0 = root, 1 = first overlay, etc.)
const StackingContext = createContext<number>(0);

/**
 * Get the current stacking level.
 * Root level is 0, first overlay is 1, nested overlay is 2, etc.
 */
export function useStackingLevel(): number {
  return useContext(StackingContext);
}

/**
 * Get the z-index for the current stacking level.
 *
 * @param offset - Optional offset to add (e.g., 10 for a sub-element that needs to be slightly above)
 * @returns The calculated z-index
 */
export function useStackingZIndex(offset: number = 0): number {
  const level = useStackingLevel();
  // Level 0 (root) has no overlay, level 1+ gets z-index
  // Level 0: 400, Level 1: 500, Level 2: 600, etc.
  return BASE_Z_INDEX + level * Z_INDEX_INCREMENT + offset;
}

/**
 * Provider that increments the stacking level for its children.
 * Wrap overlay content with this so that any overlays opened from within
 * will automatically have a higher z-index.
 */
export function StackingLayer({ children }: { children: ReactNode }) {
  const currentLevel = useStackingLevel();
  return (
    <StackingContext.Provider value={currentLevel + 1}>
      {children}
    </StackingContext.Provider>
  );
}

/**
 * Provider that sets an explicit stacking level for its children.
 *
 * Use this for overlays that hardcode their own z-index outside the
 * StackingContext system (e.g., ProposalSidebar at z-9999). Without this,
 * child overlays that use useStackingZIndex() would get a z-index far below
 * the parent's hardcoded value, rendering behind it.
 *
 * @param level - The stacking level to set. Children calling useStackingZIndex()
 *   will compute z-index as BASE_Z_INDEX + level * Z_INDEX_INCREMENT + offset.
 */
export function StackingLevelProvider({
  level,
  children,
}: {
  level: number;
  children: ReactNode;
}) {
  return (
    <StackingContext.Provider value={level}>
      {children}
    </StackingContext.Provider>
  );
}
